import React, { useMemo, useState } from 'react';
import { useAuth } from '../auth/hooks/useAuth';
import { useAbortableFetch } from '../../hooks/useAbortableFetch';
import { submissionService } from '../../services/submissionService';
import { mapSupabaseError } from '../../utils/supabaseErrors';
import { ReviewQueueTable } from './ReviewQueueTable';
import { ReviewSubmissionModal } from './ReviewSubmissionModal';
import './MySubmissions.css';
import './IndividualSubmissions.css';

const STATUSES = [{ key: 'submitted', label: 'Pending' }, { key: 'approved', label: 'Approved' }, { key: 'rejected', label: 'Rejected' }];

// A report row's own country (per-country systems) wins over the submitter's
// home country; KPI/PI and legacy rows fall back to home.
const effectiveCountry = (r) => r.countries?.name || r.submitter?.countries?.name;

export function IndividualSubmissionsPage() {
  const { user, userRole } = useAuth();
  const canDisapprove = ['Admin', 'Manager'].includes(userRole?.role);
  const [status, setStatus] = useState('submitted');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [period, setPeriod] = useState('');
  const [country, setCountry] = useState('');
  const [teamLead, setTeamLead] = useState('');
  const [reviewing, setReviewing] = useState(null);

  const load = async (ctx = {}) => {
    const isCancelled = ctx.isCancelled ?? (() => false);
    try {
      setLoading(true);
      const data = await submissionService.listForReview(status);
      if (isCancelled()) return;
      setRows(data);
    } catch (e) {
      if (!isCancelled()) setError(mapSupabaseError(e));
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  useAbortableFetch(({ isCancelled }) => { if (user) load({ isCancelled }); }, [user?.id, status]);
  const refresh = () => load();

  const countries = useMemo(
    () => [...new Set(rows.map(effectiveCountry).filter(Boolean))].sort(),
    [rows]
  );

  const teamLeads = useMemo(
    () => [...new Set(rows.map((r) => r.submitter?.team_lead?.name).filter(Boolean))].sort(),
    [rows]
  );

  const filtered = useMemo(() => rows.filter((r) => {
    if (name && !(r.submitter?.name || '').toLowerCase().includes(name.toLowerCase())) return false;
    if (type && r.type !== type) return false;
    if (period && r.period !== period) return false;
    if (country && effectiveCountry(r) !== country) return false;
    if (teamLead && r.submitter?.team_lead?.name !== teamLead) return false;
    return true;
  }), [rows, name, type, period, country, teamLead]);

  return (
    <div className="individual-submissions">
      <h2>Individual Submissions</h2>
      {error && <div className="error-message" role="alert">{error}</div>}

      <div className="review-filters">
        <span className="review-status-toggle" role="tablist" aria-label="Status">
          {STATUSES.map((s) => (
            <button key={s.key} type="button" role="tab" aria-selected={status === s.key}
              className={status === s.key ? 'active' : ''} onClick={() => setStatus(s.key)}>{s.label}</button>
          ))}
        </span>
        <input type="text" placeholder="Search name…" value={name} onChange={(e) => setName(e.target.value)} aria-label="Search name" />
        <select value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">All types</option>
          <option value="kpi">KPI</option>
          <option value="performance">Performance</option>
          <option value="report">Reports</option>
        </select>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period">
          <option value="">All periods</option><option value="Weekly">Weekly</option><option value="Monthly">Monthly</option><option value="Quarterly">Quarterly</option>
        </select>
        <select value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country">
          <option value="">All countries</option>
          {countries.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={teamLead} onChange={(e) => setTeamLead(e.target.value)} aria-label="Team Lead">
          <option value="">All team leads</option>
          {teamLeads.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {loading ? <p className="loading-msg">Loading…</p> : (
        <ReviewQueueTable rows={filtered} status={status} onReview={(r) => setReviewing(r)} />
      )}

      {reviewing && (
        <ReviewSubmissionModal
          submission={reviewing}
          canDisapprove={canDisapprove}
          onActioned={() => { setReviewing(null); refresh(); }}
          onClose={() => setReviewing(null)}
        />
      )}
    </div>
  );
}
